import { useQuery, useMutation } from '@tanstack/vue-query';
import { validatorFactory } from '@laam/lib/validator';
import * as Sentry from '@sentry/vue';
import {
	type CartResponseNewType,
	CartResponseNew,
} from '~/components/cart/types';

const cartResponseValidator =
	validatorFactory<CartResponseNewType>(CartResponseNew);
interface CartPayload {
	cartHash?: string;
	stableId: string;
	update: boolean;
	payload: {
		line_items: {
			id: number;
			quantity: number;
		}[];
	};
	oldCartId?: string | null;
}
const cartResponseValidation = (
	response: any,
	functionName:
		| 'useCreateCart'
		| 'useGetCartQuery'
		| 'useAddUpdateCart'
		| 'useRemoveCartItem',
) => {
	try {
		return cartResponseValidator.verify(response);
	} catch (err) {
		console.log(err);
		const error = new Error(`[${functionName}] Failed to validate response`);
		Sentry.captureException(error, (scope) => {
			scope.setContext('errors', {
				error: err,
			});
			return scope;
		});
		throw error;
	}
};
export const useCreateCart = () => {
	const { data, isPending, error, mutateAsync } = useMutation({
		retry: 3,
		mutationFn: async (payload: CartPayload) => {
			const api = createApiInstance();
			let url = `v1/cart?stable_id=${payload.stableId}`;
			if (payload.oldCartId !== null && payload.oldCartId !== undefined) {
				url += `&old_cart_id=${payload.oldCartId}`;
			}
                        const response = await api.post(url, payload.payload);
                        if (response.status === 200) {
                                const data = {
                                        ...response.data,
                                        code: response.status,
                                };
                                return cartResponseValidation(data, 'useCreateCart');
                        }
			const error = new Error('[useCreateCart] Failed to create cart');
			Sentry.captureException(error, (scope) => {
				scope.setContext('data', {
					response: response.data,
				});
				return scope;
			});
			throw error;
		},
	});
	return { data, isPending, error, mutateAsync };
};
export const useGetCartQuery = (
	stableId: Ref<string>,
	cartHash: Ref<string>,
	loganId: Ref<number>,
	shouldMigrateCartItems: Ref<boolean>,
) => {
	const enabled = computed(
		() =>
			!shouldMigrateCartItems.value && (!!cartHash.value || !!stableId.value),
	);
	const { data, isLoading, error, refetch } = useQuery({
		queryKey: ['loganId', loganId],
		staleTime: 300000, // 5 minutes
		gcTime: 1,
		refetchOnMount: true,
		refetchOnWindowFocus: true,
		retry: 1,
		enabled: enabled,
		queryFn: async () => {
			const api = createApiInstance();
			let url = 'v1/cart';
			if (cartHash.value) {
				url += `/${cartHash.value}?stable_id=${stableId.value}`;
			} else {
				url += `/null?stable_id=${stableId.value}`;
			}
			const response = await api.get(url);
			if (response.status === 200) {
				return cartResponseValidation(response.data, 'useGetCartQuery');
			}
			const error = new Error('[useGetCartQuery] Failed to fetch cart');
			Sentry.captureException(error, (scope) => {
				scope.setContext('data', {
					response: response.data,
				});
				return scope;
			});
			throw error;
		},
	});
	return { data, isLoading, error, refetch };
};

export const useAddUpdateCart = () => {
	const { data, isPending, error, mutateAsync } = useMutation({
		retry: 3,
		mutationFn: async (cartPayload: CartPayload) => {
			const api = createApiInstance();
			const response = await api.put(
				`v1/cart/${cartPayload.cartHash}?stable_id=${cartPayload.stableId}&update=${cartPayload.update}`,
				cartPayload.payload,
			);
			if (response.status === 200) {
				const data = {
					...response.data,
					code: response.status, // send this from backend
				};
				return cartResponseValidation(data, 'useAddUpdateCart');
			}
			const error = new Error('[useAddUpdateCart] Failed to add item to cart');
			Sentry.captureException(error, (scope) => {
				scope.setContext('data', {
					response: response.data,
				});
				return scope;
			});
			throw error;
		},
	});
	return { data, isPending, error, mutateAsync };
};
interface RemoveCartPayload {
	cartHash?: string;
	stableId: string;
	payload: { id: number[] };
}
export const useRemoveCartItem = () => {
	const { data, isPending, error, mutateAsync } = useMutation({
		retry: 3,
		mutationFn: async (cartPayload: RemoveCartPayload) => {
			const api = createApiInstance();
			const response = await api.patch(
				`v1/cart/${cartPayload.cartHash}?stable_id=${cartPayload.stableId}`,
				cartPayload.payload,
			);
			if (response.status === 200) {
				return cartResponseValidation(response.data, 'useRemoveCartItem');
			}
			const error = new Error('[useRemoveCartItem] Failed to remove item');
			Sentry.captureException(error, (scope) => {
				scope.setContext('data', {
					response: response.data,
				});
				return scope;
			});
			throw error;
		},
	});
	return { data, isPending, error, mutateAsync };
};
